import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Save, RefreshCw, Home, Phone, Globe, FileText, 
  Type, MessageSquare, Clock, MapPin
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

const CMSSiteContentPage = () => {
  const [content, setContent] = useState({
    hero: {
      badge: "Agence de communication depuis 2012",
      title: "Boostons la **visibilité** de votre entreprise",
      subtitle: "Agence de communication créative à Istres",
      cta_text: "Demander un devis gratuit",
      services_line: "Logos • Visuels • Vidéos • Sites web • Marketing"
    },
    footer: {
      tagline: "Votre partenaire créatif depuis 2009",
      description: "Votre partenaire communication à Istres depuis 2012. Création web, identité visuelle, production vidéo et marketing digital.",
      copyright: "© {year} MSC L'Agence. Tous droits réservés."
    },
    contact: {
      response_time: "Réponse sous 24h",
      availability: "Lun-Ven 9h-18h",
      cta_title: "Prêt à booster votre communication ?",
      cta_subtitle: "Parlons de votre projet"
    },
    copilote: {
      badge: "Programme Premium",
      title: "COPILOTE MSC",
      subtitle: "Accompagnement stratégique sur 12 mois",
      description: "Un partenariat exclusif pour développer votre marque de manière cohérente et impactante.",
      cta_text: "Candidater au programme"
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const res = await api.get('/settings/site-content');
      if (res.data && Object.keys(res.data).length > 1) {
        setContent(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      console.error('Error loading content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/site-content', content);
      toast.success('Contenu enregistré !');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Contenu du site</h1>
          <p className="text-slate-500">Modifiez tous les textes de votre site depuis cette page</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-cyan-500 to-blue-600">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer tout
        </Button>
      </div>

      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800">
          <strong>Astuce :</strong> Utilisez **texte** pour mettre du texte en gras (couleur d'accent).
          Exemple : "Boostons la **visibilité** de votre entreprise"
        </p>
      </div>

      <Tabs defaultValue="hero">
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="hero" className="flex items-center gap-2">
            <Home className="w-4 h-4" /> Accueil
          </TabsTrigger>
          <TabsTrigger value="contact" className="flex items-center gap-2">
            <Phone className="w-4 h-4" /> Contact
          </TabsTrigger>
          <TabsTrigger value="copilote" className="flex items-center gap-2">
            <Globe className="w-4 h-4" /> COPILOTE
          </TabsTrigger>
          <TabsTrigger value="footer" className="flex items-center gap-2">
            <FileText className="w-4 h-4" /> Footer
          </TabsTrigger>
        </TabsList>

        {/* Hero Section */}
        <TabsContent value="hero" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="w-5 h-5 text-cyan-500" />
                Section Hero (Accueil)
              </CardTitle>
              <CardDescription>
                Textes principaux de la page d'accueil
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Badge (texte au-dessus du titre)</Label>
                <Input
                  value={content.hero?.badge || ''}
                  onChange={(e) => updateSection('hero', 'badge', e.target.value)}
                  placeholder="Agence de communication depuis 2012"
                />
                <p className="text-xs text-slate-500 mt-1">Petit texte affiché au-dessus du titre principal</p>
              </div>

              <div>
                <Label>Titre principal</Label>
                <Textarea
                  value={content.hero?.title || ''}
                  onChange={(e) => updateSection('hero', 'title', e.target.value)}
                  placeholder="Boostons la **visibilité** de votre entreprise"
                  rows={2}
                />
                <p className="text-xs text-slate-500 mt-1">Utilisez **texte** pour la couleur d'accent</p>
              </div>
              
              <div>
                <Label>Sous-titre</Label>
                <Input
                  value={content.hero?.subtitle || ''}
                  onChange={(e) => updateSection('hero', 'subtitle', e.target.value)}
                  placeholder="Agence de communication créative à Istres"
                />
              </div>
              
              <div>
                <Label>Ligne de services</Label>
                <Input
                  value={content.hero?.services_line || ''}
                  onChange={(e) => updateSection('hero', 'services_line', e.target.value)}
                  placeholder="Logos • Visuels • Vidéos • Sites web • Marketing"
                />
              </div>
              
              <div>
                <Label>Texte du bouton CTA</Label>
                <Input
                  value={content.hero?.cta_text || ''}
                  onChange={(e) => updateSection('hero', 'cta_text', e.target.value)}
                  placeholder="Demander un devis gratuit"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Section */}
        <TabsContent value="contact" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-cyan-500" />
                Section Contact
              </CardTitle>
              <CardDescription>
                Textes de la section contact et formulaires
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Délai de réponse</Label>
                  <Input
                    value={content.contact?.response_time || ''}
                    onChange={(e) => updateSection('contact', 'response_time', e.target.value)}
                    placeholder="Réponse sous 24h"
                  />
                </div>
                <div>
                  <Label>Disponibilité</Label>
                  <Input
                    value={content.contact?.availability || ''}
                    onChange={(e) => updateSection('contact', 'availability', e.target.value)}
                    placeholder="Lun-Ven 9h-18h"
                  />
                </div>
              </div>
              
              <div>
                <Label>Titre CTA contact</Label>
                <Input
                  value={content.contact?.cta_title || ''}
                  onChange={(e) => updateSection('contact', 'cta_title', e.target.value)}
                  placeholder="Prêt à booster votre communication ?"
                />
              </div>
              
              <div>
                <Label>Sous-titre CTA contact</Label>
                <Input
                  value={content.contact?.cta_subtitle || ''}
                  onChange={(e) => updateSection('contact', 'cta_subtitle', e.target.value)}
                  placeholder="Parlons de votre projet"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* COPILOTE Section */}
        <TabsContent value="copilote" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-orange-500" />
                Section COPILOTE
              </CardTitle>
              <CardDescription>
                Textes du programme d'accompagnement premium
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Badge</Label>
                  <Input
                    value={content.copilote?.badge || ''}
                    onChange={(e) => updateSection('copilote', 'badge', e.target.value)}
                    placeholder="Programme Premium"
                  />
                </div>
                <div>
                  <Label>Titre</Label>
                  <Input
                    value={content.copilote?.title || ''}
                    onChange={(e) => updateSection('copilote', 'title', e.target.value)}
                    placeholder="COPILOTE MSC"
                  />
                </div>
              </div>
              
              <div>
                <Label>Sous-titre</Label>
                <Input
                  value={content.copilote?.subtitle || ''}
                  onChange={(e) => updateSection('copilote', 'subtitle', e.target.value)}
                  placeholder="Accompagnement stratégique sur 12 mois"
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  value={content.copilote?.description || ''}
                  onChange={(e) => updateSection('copilote', 'description', e.target.value)}
                  placeholder="Un partenariat exclusif pour développer votre marque..."
                  rows={3}
                />
              </div>
              
              <div>
                <Label>Texte du bouton</Label>
                <Input
                  value={content.copilote?.cta_text || ''}
                  onChange={(e) => updateSection('copilote', 'cta_text', e.target.value)}
                  placeholder="Candidater au programme"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Footer Section */}
        <TabsContent value="footer" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-cyan-500" />
                Footer
              </CardTitle>
              <CardDescription>
                Textes du pied de page
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Slogan / Tagline</Label>
                <Input
                  value={content.footer?.tagline || ''}
                  onChange={(e) => updateSection('footer', 'tagline', e.target.value)}
                  placeholder="Votre partenaire créatif depuis 2009"
                />
              </div>

              <div>
                <Label>Description complète</Label>
                <Textarea
                  value={content.footer?.description || ''}
                  onChange={(e) => updateSection('footer', 'description', e.target.value)}
                  placeholder="Votre partenaire communication à Istres depuis 2012. Création web, identité visuelle, production vidéo et marketing digital."
                  rows={3}
                />
                <p className="text-xs text-slate-500 mt-1">Description affichée dans le footer sous le logo</p>
              </div>
              
              <div>
                <Label>Copyright</Label>
                <Input
                  value={content.footer?.copyright || ''}
                  onChange={(e) => updateSection('footer', 'copyright', e.target.value)}
                  placeholder="© {year} MSC L'Agence. Tous droits réservés."
                />
                <p className="text-xs text-slate-500 mt-1">{'{year}'} sera remplacé par l'année actuelle</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CMSSiteContentPage;
